import type {
  PerfWindowInitiatingEvent,
  PerfWindowInteractionMetrics,
} from './types';

interface EventTimingEntry extends PerformanceEntry {
  processingStart: number;
  processingEnd: number;
  interactionId: number;
}

export interface RecordedEventTiming {
  name: string;
  startTime: number;
  duration: number;
  processingStart: number;
  processingEnd: number;
  interactionId: number;
}

export const isLongAnimationFrameEntry = (entry: PerformanceEntry): boolean =>
  entry.entryType === 'long-animation-frame' &&
  Number.isFinite(entry.startTime) &&
  Number.isFinite(entry.duration) &&
  entry.duration >= 0;

export const isEventTimingEntry = (
  entry: PerformanceEntry
): entry is EventTimingEntry =>
  entry.entryType === 'event' &&
  'processingStart' in entry &&
  typeof entry.processingStart === 'number' &&
  'processingEnd' in entry &&
  typeof entry.processingEnd === 'number' &&
  'interactionId' in entry &&
  typeof entry.interactionId === 'number';

export const getInteractionMetrics = (
  recentEventTimings: readonly RecordedEventTiming[],
  initiatingEvent: PerfWindowInitiatingEvent | undefined
): PerfWindowInteractionMetrics => {
  if (initiatingEvent == null) {
    return {};
  }

  const initiatingEventTiming = recentEventTimings.find(
    entry =>
      entry.name === initiatingEvent.name &&
      entry.startTime === initiatingEvent.startTime
  );
  if (initiatingEventTiming == null) {
    return {};
  }

  // Zero means the browser did not assign an interaction. Grouping zero IDs
  // would combine unrelated events and attribute their latency to this trigger.
  const interactionEntries =
    initiatingEventTiming.interactionId === 0
      ? [initiatingEventTiming]
      : recentEventTimings.filter(
          entry => entry.interactionId === initiatingEventTiming.interactionId
        );
  // Multiple events can describe one interaction. Use one entry's full duration
  // and phases together so the report does not combine unrelated phase maxima.
  const slowestEntry = interactionEntries.reduce((slowest, entry) =>
    entry.duration > slowest.duration ? entry : slowest
  );

  return {
    interactionDurationMs: slowestEntry.duration,
    inputDelayMs: slowestEntry.processingStart - slowestEntry.startTime,
    processingDurationMs:
      slowestEntry.processingEnd - slowestEntry.processingStart,
    presentationDelayMs: Math.max(
      0,
      slowestEntry.startTime +
        slowestEntry.duration -
        slowestEntry.processingEnd
    ),
    interactionId: initiatingEventTiming.interactionId,
  };
};
